import { useEffect, useState } from 'react';

import AppLogger from 'utils/AppLogger';

import useAxiosPrivate from './useAxiosPrivate';
import useInView from './useInView';

/**
 * @typedef {object} returnState
 * @property {object} data - The response.data
 * @property {boolean} loading - The loading state
 * @property {boolean} error - The error state
 * @property {boolean} success - The success state
 */

/**
 * @author @buunsal
 * @description - fetch data when element is in view
 * @param {string} url - The URL to fetch data from
 * @param {object} [body] - The body of the request
 * @param {import("react").Ref} triggerElement - The element to trigger the fetch
 * @param {Function} [afterFetch] cb - The function to run after fetching the data and it takes the data as a parameter
 * @param {object} [externalData] - The data to use instead of fetching
 * @param {boolean} [externalLoading] - The loading state to use instead of fetching
 * @returns {returnState}
 * 					- data - The response.data
 * 					- loading - The loading state
 * 					- error - The error state
 * 					- success - The success state
 * @example
 * 	const { data, loading } = useLazyFetch({
 *     url: fetchURL,
 *     afterFetch,
 *     body: requestBody,
 *     triggerElement: ref });
 */
const useLazyFetch = ({ url, body, triggerElement, afterFetch, externalData, externalLoading }) => {
	const axios = useAxiosPrivate();

	const isFirstRender = useInView(triggerElement);

	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!isFirstRender) return;
		if (externalData) return;
		setLoading(true);
		axios
			.post(url, body)
			.then((res) => setData(afterFetch ? afterFetch(res.data) : res.data))
			.catch((error) => AppLogger.error(error))
			.finally(() => setLoading(false));
	}, [isFirstRender, body]);

	if (externalData) return { data: afterFetch ? afterFetch(externalData) : externalData, loading: externalLoading };
	return { data, loading };
};

export default useLazyFetch;
